import { useState } from "react";
import { Typography, TablePagination, Stack } from "@mui/material";
import { ExcelExportMenuItem } from "./ExcelExportMenuItem";
import { ExcelExportWithChibanMenuItem } from "./ExcelExportWithChibanMenuItem";
import {
  GridToolbarContainer,
  GridCsvExportMenuItem,
  GridToolbarExportContainer,
  useGridApiContext,
} from "@mui/x-data-grid";
import { FilterTradeSwitch } from "./FilterTradeSwitch";
import { type User } from "@/types/localstorage";
import { type IFilterCondition } from "../types";
import { format } from "date-fns";
import Tooltip from "@mui/material/Tooltip";
import type { GridApiCommunity } from "@mui/x-data-grid/models/api/gridApiCommunity";

interface IProps {
  disabledExportBtn: boolean;
  disabledScreening: boolean;
  hasTradeState: {
    setHasTrade: React.Dispatch<React.SetStateAction<boolean>>;
    hasTrade: boolean;
  };
  handleSearch: () => void;
  user: User | undefined;
  filterCondition: IFilterCondition;
  page: number;
  pageSize: number;
  allCount: number;
  rowsPerPageOptions: number[];
  changePage: (pageIndex: number) => void;
  changePageSize: (pageSize: number) => void;
  clearSelection: boolean;
  setClearSelection: React.Dispatch<React.SetStateAction<boolean>>;
}

const CustomToolbar: React.FC<IProps> = (props) => {
  const {
    disabledExportBtn,
    hasTradeState,
    handleSearch,
    user,
    filterCondition,
    page,
    pageSize,
    allCount,
    rowsPerPageOptions,
    changePage,
    changePageSize,
    disabledScreening,
    clearSelection,
    setClearSelection,
  } = props;

  // エクスポートボタン上のTooltipの表示/非表示の制御
  const [exportTooltipOpen, setExportTooltipOpen] = useState(false);

  // ここからならDataGridのApiRefを取得できる、おそらくDataGridコンポーネントの子コンポーネントであれば取得できる
  const gridApiRef: React.MutableRefObject<GridApiCommunity> =
    useGridApiContext();

  const getFormattedDate = (): string => {
    return format(new Date(), "yyyyMMdd");
  };

  const getFileName = (): string => {
    return `${getFormattedDate()}_不動産登記受付帳データ`;
  };

  // 選択状態クリア指示が送られている場合は選択状態をクリアし、Stateをfalseに戻す
  // 当初、下記コードをuseEffect()で囲んでいたが、clearSelectionがtrueのままfalseに戻らず
  // 次回の再レンダリング時、useEffect()の副作用処理がスキップされる問題があったため
  // 現在はuseEffect()を取り除いている
  if (clearSelection) {
    gridApiRef.current.setSelectionModel([]);
    setClearSelection(false);
  }

  return (
    <>
      <GridToolbarContainer
        sx={{ p: 0, display: "flex", justifyContent: "space-between" }}
      >
        <Stack direction={"row"}>
          {/* エクスポートボタン */}
          <Tooltip
            title={
              <>
                一覧をエクスポートするには検索結果を1,000件未満に絞り込み、その上で必要な行を選択してください。
              </>
            }
            open={exportTooltipOpen}
            placement="top"
            onMouseEnter={() => {
              if (disabledExportBtn) {
                setExportTooltipOpen(true);
              }
            }}
            onMouseLeave={() => {
              if (exportTooltipOpen) {
                setExportTooltipOpen(false);
              }
            }}
          >
            <Typography
              sx={{
                background: disabledExportBtn ? "rgba(0, 0, 0, 0.12)" : "#FFF",
                borderRadius: "4px",
                margin: "16px",
                border: disabledExportBtn ? "none" : "solid #1976d2 1px",
              }}
            >
              <GridToolbarExportContainer
                disabled={disabledExportBtn}
                sx={{
                  // https://mui.com/material-ui/react-button/#sizes と同様のpaddingを設定
                  padding: "6px 16px",

                  // アイコンとテキストのセンターラインをあわせる
                  "& > span": {
                    paddingBottom: "2px",
                    marginRight: "4px",
                  },
                }}
              >
                <GridCsvExportMenuItem
                  options={{
                    fileName: getFileName(),
                    utf8WithBom: true,
                    allColumns: true, // 全カラム出力
                    // ここでTrueにしていても"GridColDef<R,V,F>.disableExport = true"の方が優先されるみたい
                  }}
                />
                <ExcelExportMenuItem
                  options={{
                    fileName: getFileName(),
                    sheetName: "不動産登記受付帳",
                    allColumns: true,
                  }}
                />
                {user?.isFree ? (
                  <ExcelExportWithChibanMenuItem
                    condition={filterCondition}
                    options={{
                      fileName: `${getFileName()}_地番_家屋番号`,
                      sheetName: "不動産登記受付帳_地番_家屋番号",
                      allColumns: true,
                    }}
                  />
                ) : null}
              </GridToolbarExportContainer>
            </Typography>
          </Tooltip>
          {/* 売買スクリーニング */}
          <FilterTradeSwitch
            hasTrade={hasTradeState.hasTrade}
            setHasTrade={hasTradeState.setHasTrade}
            handleSearch={handleSearch}
            disabled={disabledScreening}
            isFree={user?.isFree}
          />
        </Stack>

        {/* 上部のページネーション */}
        <TablePagination
          component={"div"}
          count={allCount}
          page={page}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={(_, page) => {
            changePage(page);
          }}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => {
            changePageSize(Number(event.target.value));
          }}
          labelRowsPerPage={"ページあたりの行数:"}
          labelDisplayedRows={({ from, to, count }) => {
            return `${count.toLocaleString()}件中 ${from.toLocaleString()}件 〜 ${to.toLocaleString()}件`;
          }}
        />
      </GridToolbarContainer>
    </>
  );
};

export { CustomToolbar };
